/**
 * Paket-Auswahl (Bundle Picker)
 * Vanilla JS Custom Element - keine externen Abhängigkeiten.
 *
 * Layout: Schritt 1..N = globale Optionen (z.B. Größe, gelten für alle Einheiten aller Stufen,
 * vom Eltern-Block gerendert), Schritt N+1 = Mengen-Stufen-Karten (jede Karte rendert sich
 * selbst über content_for 'blocks', siehe custom-package-picker-tier.liquid), Schritt N+2 =
 * Pro-Stück-Override (alle Optionen, die NICHT global sind, z.B. Farbe UND Körperseite) - wird
 * HIER dynamisch aufgebaut, da der Eltern-Block tier.settings.quantity nicht direkt auslesen
 * kann (verschachtelte Theme-Blocks geben ihre Settings nur beim Rendern über content_for her,
 * nicht beim Auslesen durch den Elternblock). Jede Stufen-Karte trägt ihre Menge stattdessen
 * als data-quantity-Attribut.
 */
(function () {
  if (customElements.get('package-picker-component')) return;

  class PackagePicker extends HTMLElement {
    connectedCallback() {
      this.variants = this.parseJSON('[data-package-picker-variants]', []);
      this.perUnitOptions = this.parseJSON('[data-package-picker-per-unit-options]', []);
      this.unitLabelPrefix = this.dataset.unitLabelPrefix || 'Einheit';

      this.radios = Array.from(this.querySelectorAll('.package-picker__radio'));
      this.globalOptionInputs = Array.from(this.querySelectorAll('[data-global-option]'));
      this.unitsContainer = this.querySelector('[data-package-picker-units]');
      this.step3Number = this.unitsContainer ? this.unitsContainer.dataset.stepNumber || '3' : '3';
      this.addToCartButton = this.querySelector('.package-picker__add-to-cart');
      this.paypalButton = this.querySelector('.package-picker__paypal-button');
      this.errorEl = this.querySelector('[data-package-picker-error]');
      this.statusEl = this.querySelector('[data-package-picker-status]');

      if (!this.radios.some((radio) => radio.checked) && this.radios.length > 0) {
        this.radios[0].checked = true;
      }

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => this.handleTierChange());
      });

      this.optionValueLabels = Array.from(this.querySelectorAll('[data-option-value-label]'));

      this.globalOptionInputs.forEach((input) => {
        input.addEventListener('change', () => this.handleGlobalOptionChange());
      });

      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', () => this.addToCart());
      }

      if (this.paypalButton) {
        this.paypalButton.addEventListener('click', () => this.payWithPaypal());
      }

      this.renderUnitsForActiveTier();
    }

    parseJSON(selector, fallback) {
      const el = this.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('PackagePicker: could not parse', selector, e);
        return fallback;
      }
    }

    getGlobalSelections() {
      const selections = {};
      this.globalOptionInputs.forEach((input) => {
        if (input.checked) selections[input.dataset.optionPosition] = input.value;
      });
      return selections;
    }

    getActiveTierRadio() {
      return this.radios.find((radio) => radio.checked) || null;
    }

    handleTierChange() {
      this.renderUnitsForActiveTier();
    }

    handleGlobalOptionChange() {
      this.updateOptionValueLabels();
      this.renderUnitsForActiveTier();
    }

    updateOptionValueLabels() {
      const selections = this.getGlobalSelections();
      this.optionValueLabels.forEach((label) => {
        const value = selections[label.dataset.optionPosition];
        if (value) label.textContent = value;
      });
    }

    static isColorOption(name) {
      const lower = (name || '').toLowerCase();
      return lower === 'farbe' || lower === 'color' || lower === 'colour';
    }

    static colorSwatchHex(value) {
      const map = {
        schwarz: '#111111', black: '#111111',
        weiß: '#ffffff', weiss: '#ffffff', white: '#ffffff',
        grau: '#9e9e9e', gray: '#9e9e9e', grey: '#9e9e9e',
        blau: '#1d4ed8', blue: '#1d4ed8',
        rot: '#dc2626', red: '#dc2626',
        grün: '#16a34a', gruen: '#16a34a', green: '#16a34a',
        beige: '#e8dcc8',
        braun: '#5b3a29', brown: '#5b3a29',
        silber: '#c0c0c0', silver: '#c0c0c0',
        gold: '#d4af37',
        rosa: '#f4a6c1', pink: '#f4a6c1',
        lila: '#7c3aed', violett: '#7c3aed', purple: '#7c3aed',
        gelb: '#f5d90a', yellow: '#f5d90a',
        orange: '#f97316',
        navy: '#1e2a5e', marine: '#1e2a5e',
        anthrazit: '#383838',
        creme: '#f3e5c8', cream: '#f3e5c8',
        türkis: '#1cb5b0', turkis: '#1cb5b0', turquoise: '#1cb5b0',
      };
      return map[(value || '').toLowerCase()] || '#cccccc';
    }

    renderUnitsForActiveTier() {
      const radio = this.getActiveTierRadio();
      const quantity = radio ? Number(radio.dataset.quantity) || 1 : 1;
      const showStep3 = this.perUnitOptions.length > 0;

      this.unitsContainer.innerHTML = '';
      this.unitRows = [];

      if (showStep3) {
        this.unitsContainer.hidden = false;

        const stepHeader = document.createElement('div');
        stepHeader.className = 'package-picker__step';
        stepHeader.innerHTML =
          '<div class="package-picker__step-header">' +
          '<span class="package-picker__step-number">' + this.step3Number + '</span>' +
          '<span class="package-picker__step-title"></span>' +
          '</div>';
        stepHeader.querySelector('.package-picker__step-title').textContent = this.perUnitOptions
          .map((option) => option.name)
          .join(' & ');
        this.unitsContainer.appendChild(stepHeader);

        for (let i = 0; i < quantity; i++) {
          const unit = document.createElement('div');
          unit.className = 'package-picker__unit';

          if (quantity > 1) {
            const label = document.createElement('span');
            label.className = 'package-picker__unit-label';
            label.textContent = this.unitLabelPrefix + ' ' + (i + 1);
            unit.appendChild(label);
          }

          const optionsWrapper = document.createElement('div');
          optionsWrapper.className = 'package-picker__unit-options';

          this.perUnitOptions.forEach((option) => {
            const values = option.values || [];
            const isColor = PackagePicker.isColorOption(option.name);
            const groupName = 'unit-opt-' + this.id + '-' + option.position + '-' + i;
            const defaultIndex = values.length > 0 ? i % values.length : 0;

            const pills = document.createElement('div');
            pills.className = isColor ? 'package-picker__swatches' : 'package-picker__pills';

            values.forEach((value, valueIndex) => {
              const pillLabel = document.createElement('label');
              pillLabel.className = isColor
                ? 'package-picker__swatch'
                : 'package-picker__pill package-picker__pill--small';

              const input = document.createElement('input');
              input.type = 'radio';
              input.name = groupName;
              input.value = value;
              input.dataset.unitOption = 'true';
              input.dataset.optionPosition = String(option.position);
              if (valueIndex === defaultIndex) input.checked = true;
              input.addEventListener('change', () => this.updateAddToCartState());

              pillLabel.appendChild(input);

              if (isColor) {
                const circle = document.createElement('span');
                circle.className = 'package-picker__swatch-circle';
                circle.style.background = PackagePicker.colorSwatchHex(value);
                circle.title = value;
                pillLabel.appendChild(circle);
              } else {
                const span = document.createElement('span');
                span.textContent = value;
                pillLabel.appendChild(span);
              }

              pills.appendChild(pillLabel);
            });

            optionsWrapper.appendChild(pills);
          });

          unit.appendChild(optionsWrapper);
          this.unitsContainer.appendChild(unit);
          this.unitRows.push(unit);
        }
      } else {
        this.unitsContainer.hidden = true;
      }

      this.updateAddToCartState();
    }

    resolveVariantForUnit(unitOverrides) {
      const combined = Object.assign({}, this.getGlobalSelections(), unitOverrides || {});
      const positions = Object.keys(combined)
        .map(Number)
        .sort((a, b) => a - b);
      const selectedValues = positions.map((position) => combined[position]);

      return this.variants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(
          (value) => value !== null && value !== undefined
        );
        return (
          variantOptions.length === selectedValues.length &&
          variantOptions.every((value, index) => value === selectedValues[index])
        );
      });
    }

    getActiveUnitVariants() {
      const radio = this.getActiveTierRadio();
      const quantity = radio ? Number(radio.dataset.quantity) || 1 : 1;

      if (this.unitRows && this.unitRows.length > 0) {
        return this.unitRows.map((unit) => {
          const overrides = {};
          unit.querySelectorAll('[data-unit-option]:checked').forEach((input) => {
            overrides[input.dataset.optionPosition] = input.value;
          });
          return this.resolveVariantForUnit(overrides);
        });
      }

      const variant = this.resolveVariantForUnit({});
      return new Array(quantity).fill(variant);
    }

    updateAddToCartState() {
      const allResolved = this.isReady();
      if (this.addToCartButton) this.addToCartButton.disabled = !allResolved;
      this.updateNativePaymentButton(allResolved);
      this.notifyUpdate();
    }

    updateNativePaymentButton(allResolved) {
      const nativeContainer = this.querySelector('[data-package-picker-native-payment]');
      if (!nativeContainer) return;

      const variants = this.getActiveUnitVariants();
      const singleVariant =
        allResolved && variants.length > 0 && variants.every((variant) => variant && variant.id === variants[0].id)
          ? variants[0]
          : null;

      if (singleVariant) {
        const idInput = nativeContainer.querySelector('[data-package-picker-payment-variant-id]');
        const qtyInput = nativeContainer.querySelector('[data-package-picker-payment-quantity]');
        if (idInput && idInput.value !== String(singleVariant.id)) {
          idInput.value = singleVariant.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (qtyInput && qtyInput.value !== String(variants.length)) {
          qtyInput.value = variants.length;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        nativeContainer.hidden = false;
        if (this.paypalButton) this.paypalButton.hidden = true;
      } else {
        nativeContainer.hidden = true;
        if (this.paypalButton) {
          this.paypalButton.hidden = false;
          this.paypalButton.disabled = !allResolved;
        }
      }
    }

    isReady() {
      const variants = this.getActiveUnitVariants();
      return variants.length > 0 && variants.every((variant) => variant && variant.available);
    }

    getSummary() {
      const activeCard = this.querySelector('.package-picker__card:has(.package-picker__radio:checked)');
      const price = activeCard ? activeCard.querySelector('.package-picker__price')?.textContent.trim() : null;
      const comparePrice = activeCard
        ? activeCard.querySelector('.package-picker__compare-price')?.textContent.trim()
        : null;
      const radio = this.getActiveTierRadio();
      const quantity = radio ? Number(radio.dataset.quantity) || 1 : 1;

      return { price: price || null, comparePrice: comparePrice || null, quantity: quantity, ready: this.isReady() };
    }

    notifyUpdate() {
      document.dispatchEvent(
        new CustomEvent('package-picker:update', {
          bubbles: true,
          detail: { sourceId: this.id, summary: this.getSummary() },
        })
      );
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    getSectionsToUpdate() {
      const ids = new Set();
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
      });
      const cartIconSection = document.querySelector('[id^="shopify-section-"] cart-icon');
      if (cartIconSection) {
        const section = cartIconSection.closest('[id^="shopify-section-"]');
        if (section) ids.add(section.id.replace('shopify-section-', ''));
      }
      const ownSection = this.closest('[id^="shopify-section-"]');
      if (ownSection) ids.add(ownSection.id.replace('shopify-section-', ''));
      return Array.from(ids);
    }

    postToCart() {
      const variants = this.getActiveUnitVariants();
      if (variants.length === 0 || variants.some((variant) => !variant)) return Promise.reject(new Error('no-variants'));

      const items = variants.map((variant) => ({ id: Number(variant.id), quantity: 1 }));
      const sections = this.getSectionsToUpdate();

      this.showError('');

      return fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sections }),
      }).then((response) =>
        response.json().then((data) => {
          if (!response.ok) throw data;
          return data;
        })
      );
    }

    addToCart() {
      if (!this.addToCartButton) return;
      this.addToCartButton.disabled = true;
      const originalText = this.addToCartButton.textContent;
      this.addToCartButton.textContent = 'Wird hinzugefügt...';

      this.postToCart()
        .then((data) => {
          const sectionsHtml = data.sections || {};
          Object.keys(sectionsHtml).forEach((sectionId) => {
            const sectionElement = document.getElementById('shopify-section-' + sectionId);
            if (sectionElement && sectionsHtml[sectionId]) {
              if (typeof window.morphSection === 'function') {
                window.morphSection(sectionId, sectionsHtml[sectionId]);
              } else {
                sectionElement.innerHTML = sectionsHtml[sectionId];
              }
            }
          });

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: data,
                sourceId: this.id,
                data: { source: 'package-picker', sections: sectionsHtml },
              },
            })
          );

          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          }

          if (this.statusEl) this.statusEl.textContent = 'Zum Warenkorb hinzugefügt.';
        })
        .catch((error) => {
          console.error('PackagePicker: cart add error', error);
          this.showError((error && error.message) || 'Konnte nicht zum Warenkorb hinzugefügt werden.');
        })
        .finally(() => {
          this.addToCartButton.textContent = originalText;
          this.updateAddToCartState();
        });
    }

    payWithPaypal() {
      if (!this.paypalButton) return;
      this.paypalButton.disabled = true;
      const originalHtml = this.paypalButton.innerHTML;
      this.paypalButton.textContent = 'Weiterleitung...';

      this.postToCart()
        .then(() => {
          const rootUrl = (window.Theme && window.Theme.routes && window.Theme.routes.root_url) || '/';
          window.location.href = rootUrl.replace(/\/$/, '') + '/checkout';
        })
        .catch((error) => {
          console.error('PackagePicker: paypal checkout error', error);
          this.showError((error && error.message) || 'Konnte nicht zur Kasse weiterleiten.');
          this.paypalButton.innerHTML = originalHtml;
          this.updateAddToCartState();
        });
    }
  }

  customElements.define('package-picker-component', PackagePicker);
})();
